const TRANSITIONS = {
    ACTIVE: {
        DEGRADED_SIGNAL: 'DEGRADED',
        OFFLINE: 'RECONNECTING',
    },
    DEGRADED: {
        OFFLINE: 'RECONNECTING',
        RECOVERED: 'ACTIVE',
        DEGRADED_SIGNAL: 'DEGRADED',
    },
    RECONNECTING: {
        RECOVERED: 'VERIFYING',
        WINDOW_EXPIRED: 'INTERRUPTED',
    },
    VERIFYING: {
        VERIFIED: 'ACTIVE',
        OFFLINE: 'RECONNECTING', // lost again mid-verify
    },
    INTERRUPTED: {}, // terminal
};
/** Pure transition. Unknown event for the current state → unchanged. RESET → ACTIVE (except terminal). */
export function connectionReducer(state, event) {
    var _a, _b;
    if (event === 'RESET') {
        return state === 'INTERRUPTED' ? state : 'ACTIVE';
    }
    return (_b = (_a = TRANSITIONS[state]) === null || _a === void 0 ? void 0 : _a[event]) !== null && _b !== void 0 ? _b : state;
}
/** Remaining seconds in the reconnect window (never negative). */
export function getReconnectCountdown(windowSeconds, elapsedSeconds) {
    return Math.max(0, Math.ceil(windowSeconds - elapsedSeconds));
}
/**
 * NR-5/NR-6: should the reconnect sequence give up and end the interview? The
 * ONLY reason to stop is the full wall-clock window elapsing — never an attempt
 * count — so a slow-but-recoverable backend rebuild can't end a recoverable
 * interview after a few quick tries. (This is now the single behavior; the old
 * attempt-count path and its REACT_APP_OPENAI_RECONNECT_WINDOW_BOUND flag were
 * removed per the no-feature-flags rule.) Pure/testable.
 */
export function reconnectExhausted(opts) {
    return opts.elapsedMs >= opts.windowMs;
}
/** Default-ON flag: enabled unless explicitly set to a falsy value. */
function flagDefaultOn(raw) {
    const v = String(raw !== null && raw !== void 0 ? raw : '')
        .trim()
        .toLowerCase();
    if (v === '')
        return true; // unset ⇒ on by default
    return !(v === '0' || v === 'false' || v === 'no' || v === 'off');
}
/** Network-resilience UX. Default ON; set REACT_APP_NETWORK_PAUSE_ENABLED=false to disable. */
export function isNetworkPauseEnabled() {
    return flagDefaultOn(process.env.REACT_APP_NETWORK_PAUSE_ENABLED);
}
/**
 * T-D7: OpenAI-drop signaling. When on, the realtime context tells the backend
 * `openai_connection_lost` / `_restored` as the OpenAI audio link drops/recovers,
 * so the backend can pause/resume the clock for dead air the NR monitor can't
 * see. Default ON; set REACT_APP_OPENAI_DROP_PAUSE_ENABLED=false to disable.
 */
export function isOpenAiDropPauseEnabled() {
    return flagDefaultOn(process.env.REACT_APP_OPENAI_DROP_PAUSE_ENABLED);
}
/**
 * Only declare "connection lost" (the blocking RECONNECTING overlay) when the
 * realtime link is GENUINELY down. The audio path is browser↔OpenAI directly,
 * so while the OpenAI WS is OPEN the candidate can still converse — a lagging
 * backend pong or a spurious browser `offline` blip must NOT throw the blocking
 * popup (only the soft "unstable" pill). Default ON; set
 * REACT_APP_CONNECTION_LOST_REQUIRES_SOCKET_DOWN_ENABLED=false to restore the
 * old purely-timing-based behaviour.
 */
export function isConnectionLostRequiresSocketDownEnabled() {
    return flagDefaultOn(process.env.REACT_APP_CONNECTION_LOST_REQUIRES_SOCKET_DOWN_ENABLED);
}
/**
 * Genuine-offline override for the "requires socket down" guard above. That
 * guard suppresses the overlay while the OpenAI WS `readyState === OPEN` — but
 * `readyState` is a STALE local flag: on a real internet drop the browser↔OpenAI
 * socket stays OPEN for many seconds (no network to deliver a close), so the
 * guard swallowed EVERY detection path and the candidate kept burning time on a
 * dead link with no "Connection lost" popup and no freeze. When enabled, two
 * genuine-loss signals bypass the `openAiOpen` short-circuit and declare the
 * connection lost regardless of the stale readyState:
 *   (1) the browser reports `navigator.onLine === false` (a real local NIC/Wi-Fi
 *       drop ⇒ the OpenAI socket is dead too, whatever readyState claims), and
 *   (2) a HARD heartbeat-miss threshold — the backend has been totally silent
 *       far longer than a slow turn could explain (see hardMissLimit).
 * Default ON; set REACT_APP_CONNECTION_LOST_ON_BROWSER_OFFLINE_ENABLED=false to
 * restore the pure readyState-gated behaviour.
 */
export function isConnectionLostOnBrowserOfflineEnabled() {
    return flagDefaultOn(process.env.REACT_APP_CONNECTION_LOST_ON_BROWSER_OFFLINE_ENABLED);
}
/**
 * Both-sockets-open guard for the HARD heartbeat-miss force path. A hard miss
 * (backend silent for many pings) is normally allowed to force the "connection
 * lost" overlay even while the OpenAI WS reports OPEN, because on a REAL drop the
 * readyState lies (stays stale-OPEN). But when BOTH the backend AND OpenAI
 * sockets are genuinely OPEN, the transports are alive on all three legs
 * (browser↔backend and browser↔OpenAI) — the candidate can still talk to the AI
 * and reach the server — so a run of unanswered pongs means the link is merely
 * SLOW/POOR, not lost. Throwing the blocking overlay there is a false positive
 * (observed on a backgrounded/throttled tab whose heartbeat stalled while both
 * sockets stayed OPEN). When enabled, a hard miss with both sockets OPEN grades
 * the connection "poor" (soft pill) instead of forcing offline; the overlay is
 * still forced the moment either socket actually goes down, or the browser
 * reports offline. Default ON; set
 * REACT_APP_HARD_MISS_REQUIRES_SOCKET_DOWN_ENABLED=false to restore the old
 * force-on-hard-miss-regardless behaviour.
 */
export function isHardMissRequiresSocketDownEnabled() {
    return flagDefaultOn(process.env.REACT_APP_HARD_MISS_REQUIRES_SOCKET_DOWN_ENABLED);
}
/**
 * WS-2 (join-flash): do NOT arm the blocking "connection lost" path until the
 * OpenAI realtime link has been OPEN at least once. At join the monitor arms as
 * soon as `interviewEndTime` is set, but the backend then spends several seconds
 * (~12s observed) building the OpenAI session, during which openAiOpen is still
 * false — so the `requireSocketDown` suppression is inert and the backend's
 * lagging startup pongs / a transient `offline` blip trip the blocking overlay
 * over the "Starting interview…" boot, flashing "Connection lost" before the
 * greeting. When on, a NON-forced goOffline is suppressed (soft pill only) until
 * the OpenAI socket has been OPEN once. Genuine-loss signals (navigator offline,
 * hard heartbeat-miss) still surface immediately, so a real outage at join is
 * not masked. Default ON; set REACT_APP_JOIN_MONITOR_ARM_AFTER_AI_ENABLED=false
 * to restore the old behaviour.
 */
export function isJoinMonitorArmAfterAiEnabled() {
    return flagDefaultOn(process.env.REACT_APP_JOIN_MONITOR_ARM_AFTER_AI_ENABLED);
}
/**
 * WS-1 (netq smoothing): grade the connection-quality pill from a WINDOW of RTT
 * samples with hysteresis, instead of flipping on every single pong. The pong
 * RTT rides the backend's serial WS loop — the assistant-turn classifier (~6s)
 * delays it — so one slow turn on a healthy 30 Mbps link used to flash a "poor
 * connection" pill that the next fast pong cleared, strobing the UI on tiny
 * fluctuations. When on: enter "unstable"/"poor" only when several of the last N
 * samples exceed the threshold, and recover to stable only after a few
 * consecutive fast samples (asymmetric hysteresis). RTT samples measured while
 * an assistant turn is active are skipped from grading (they measure backend
 * busyness, not the network). Default ON; set REACT_APP_NETQ_SMOOTHING_ENABLED=
 * false to restore the old single-sample grading.
 */
export function isNetqSmoothingEnabled() {
    return flagDefaultOn(process.env.REACT_APP_NETQ_SMOOTHING_ENABLED);
}
/**
 * Coding editor closes ONLY on a user button press (Submit / Skip / Close) —
 * never from a backend classifier event. The coding lifecycle is inferred by a
 * post-turn LLM classifier reading the AI's spoken words, which can emit
 * `end_coding_question` or `start_coding_question phase=solution_explanation`
 * (the walkthrough) with no actual submission — and the FE obeyed by closing the
 * editor (the `start` path called `toggleCodeEditor(false)` for any non-
 * problem_statement phase). That auto-closed the editor while the candidate had
 * written nothing and never submitted. When enabled, backend events may only
 * OPEN the editor (on `problem_statement`); they can never close an editor the
 * candidate still has open — only the Submit/Skip/Close buttons do. Default ON;
 * set REACT_APP_CODING_EDITOR_BUTTON_ONLY_CLOSE_ENABLED=false to restore the old
 * backend-event-driven close.
 */
export function isCodingEditorButtonOnlyCloseEnabled() {
    return flagDefaultOn(process.env.REACT_APP_CODING_EDITOR_BUTTON_ONLY_CLOSE_ENABLED);
}
// NR-6: REACT_APP_OPENAI_RECONNECT_WINDOW_BOUND_ENABLED removed — window-bound
// reconnect is now the single, permanent behavior (see reconnectExhausted).
/**
 * Reconnect media recovery. After a LONG network drop (≈minutes) the cause is
 * often device sleep, mobile tab suspension, or the OS reclaiming the camera /
 * mic — any of which ENDS the candidate's MediaStreamTracks. The capture graph
 * is built once at interview start and never re-validated on reconnect, so it
 * stays wired to those dead tracks: the candidate's voice never reaches OpenAI
 * again (and, because the server VAD then never hears them, the AI also goes
 * silent after its greeting), and the camera preview / recording freezes. When
 * enabled, the realtime context re-checks the mic + camera tracks on every
 * interview resume and re-acquires whichever ended.
 * Default ON; set REACT_APP_RECONNECT_MEDIA_RECOVERY_ENABLED=false to disable.
 */
export function isReconnectMediaRecoveryEnabled() {
    return flagDefaultOn(process.env.REACT_APP_RECONNECT_MEDIA_RECOVERY_ENABLED);
}
/**
 * BUG-1: never auto-END the interview from the OpenAI-reconnect path while we can
 * still recover within the reconnect window. The backend OpenAI session rebuild
 * can take longer than a single watchdog window (observed ~12s, retried up to 4×);
 * ending on a small retry budget killed recoverable interviews. When enabled, an
 * exhausted retry budget surfaces a recoverable Rejoin and keeps retrying to the
 * wall-clock backstop instead of finalizing. Default ON; set
 * REACT_APP_OPENAI_RECONNECT_NO_AUTO_END_ENABLED=false to restore the old behavior.
 */
export function isOpenAiReconnectNoAutoEndEnabled() {
    return flagDefaultOn(process.env.REACT_APP_OPENAI_RECONNECT_NO_AUTO_END_ENABLED);
}
/**
 * BUG-2: when the interview ends/finalizes, clear the reconnect UI surfaces
 * (reconnectError, isAiReconnecting, session.created watchdog) and suppress the
 * ConnectivityOverlay/Rejoin so a stale "Reconnecting…/Rejoin" dialog can't
 * linger over the finalizing/completed screen. Default ON; set
 * REACT_APP_RECONNECT_UI_CLEAR_ON_END_ENABLED=false to restore the old behavior.
 */
export function isReconnectUiClearOnEndEnabled() {
    return flagDefaultOn(process.env.REACT_APP_RECONNECT_UI_CLEAR_ON_END_ENABLED);
}
/**
 * Phase 7: authoritative timer-pause freeze. The old freeze was inferred only
 * from the connection-monitor state, which misses an OpenAI-only drop (monitor
 * stays ACTIVE) and lags other drops by the detection delay — so the FE clock
 * drained during a backend pause ("timer shifts to minutes less"). When enabled,
 * the freeze also honors the backend's authoritative `timer_state {paused}` and
 * the OpenAI reconnect state. Default ON; set
 * REACT_APP_TIMER_AUTHORITATIVE_PAUSE_ENABLED=false to restore the old behavior.
 */
export function isTimerAuthoritativePauseEnabled() {
    return flagDefaultOn(process.env.REACT_APP_TIMER_AUTHORITATIVE_PAUSE_ENABLED);
}
/**
 * Phase 8 (BUG-8): client-initiated finalize. A frontend end via
 * endInterviewInternal (OpenAI/backend reconnect exhaustion, wall-clock backstop)
 * assembles the video but never tells the backend to finalize, leaving the record
 * stuck "in-progress" (no ended_at / no report). When enabled, the FE sends a
 * `client_finalize` on every client-initiated end so the backend writes ended_at
 * + the report. Default ON; set REACT_APP_CLIENT_FINALIZE_ENABLED=false to disable.
 */
export function isClientFinalizeEnabled() {
    return flagDefaultOn(process.env.REACT_APP_CLIENT_FINALIZE_ENABLED);
}
/**
 * Pure decision: should the candidate-facing countdown be frozen right now?
 *
 * ``monitorPaused`` (RECONNECTING/INTERRUPTED) is the original signal. With the
 * authoritative flag on we additionally freeze on the backend's pause signal
 * (``serverPaused`` — covers the OpenAI-only drop the monitor can't see) and
 * while the OpenAI link is reconnecting (``isAiReconnecting`` — belt-and-
 * suspenders if the pause signal is delayed/lost). Flag off ⇒ exactly the old
 * monitor-only behavior. Kept pure for unit testing.
 */
export function computeTimerPaused(status, isAiReconnecting, serverPaused, authoritativeEnabled) {
    const monitorPaused = status === 'RECONNECTING' || status === 'INTERRUPTED';
    if (!authoritativeEnabled)
        return monitorPaused;
    return Boolean(serverPaused) || Boolean(isAiReconnecting) || monitorPaused;
}
/**
 * Pure predicate: does this media track need re-acquiring before we can use it
 * again? A missing track (the stream was lost entirely) or one whose
 * `readyState` is `'ended'` (device sleep / suspended tab / device reclaimed) is
 * unrecoverable in place and must be replaced. A `'live'` track is reused as-is.
 * Works for both audio and video tracks; typed loosely so it is unit-testable
 * without a real MediaStreamTrack.
 */
export function mediaTrackNeedsReacquire(track) {
    return !track || track.readyState === 'ended';
}
